import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import SiteChrome from "@/components/candidate/SiteChrome";
import {
    PhoneInput,
    OtpInput,
} from "@/components/form/PhoneOtpInputs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldCheck, UserPlus, Loader2 } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({
    baseURL: `${BASE}/api`,
    headers: { "Content-Type": "application/json" },
    timeout: 20000,
});

function apiError(err) {
    const d = err?.response?.data?.detail;
    if (typeof d === "string") return d;
    if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
    return err?.message || "Something went wrong.";
}

export default function AttendanceVerify() {
    const navigate = useNavigate();
    const [tab, setTab] = useState("verify");
    const [busy, setBusy] = useState(false);
    const [colleges, setColleges] = useState([]);
    const [jobRoles, setJobRoles] = useState([]);

    // Verify form
    const [vPhone, setVPhone] = useState("");
    const [vPhoneValid, setVPhoneValid] = useState(false);
    const [vOtp, setVOtp] = useState("");
    const [vOtpValid, setVOtpValid] = useState(false);

    // Register form
    const [reg, setReg] = useState({
        name: "",
        phone: "",
        email: "",
        college_name: "",
        course: "",
        branch: "",
        role: "",
    });
    const [regPhoneValid, setRegPhoneValid] = useState(false);

    useEffect(() => {
        api.get("/public/colleges")
            .then(({ data }) => setColleges(data || []))
            .catch(() => setColleges([]));
        api.get("/public/job-roles")
            .then(({ data }) => setJobRoles(data || []))
            .catch(() => setJobRoles([]));
    }, []);

    const submitVerify = async (e) => {
        e.preventDefault();
        if (!vPhoneValid || !vOtpValid) return;
        setBusy(true);
        try {
            const { data } = await api.post("/public/attendance/verify", {
                phone: vPhone,
                otp: vOtp,
            });
            toast.success("Attendance verified");
            navigate("/verify/details", {
                state: {
                    candidate: data.candidate,
                    verification_token: data.verification_token,
                },
            });
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setBusy(false);
        }
    };

    const submitRegister = async (e) => {
        e.preventDefault();
        if (!regPhoneValid) return;
        setBusy(true);
        try {
            const { data } = await api.post("/public/attendance/register", reg);
            navigate("/verify/thank-you", {
                state: { candidate: data.candidate, selfRegistered: true },
            });
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <SiteChrome eyebrow="Interview Attendance">
            <div
                data-testid="attend-page"
                className="max-w-md mx-auto px-6 py-10 md:py-16"
            >
                <div
                    data-testid="attend-card"
                    className="bg-white border border-[#0F1F38]/10 shadow-[0_1px_0_rgba(15,31,56,0.02),0_20px_40px_-24px_rgba(15,31,56,0.15)] p-6 md:p-8"
                >
                    {/* Card header — replaces the removed page banner */}
                    <div className="text-center mb-6">
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#F7F5F0] border border-[#0F1F38]/10 mb-4">
                            <ShieldCheck
                                size={20}
                                strokeWidth={1.5}
                                className="text-[#0F1F38]"
                            />
                        </div>
                        <h2 className="font-display text-2xl md:text-[28px] leading-tight text-[#0F1F38]">
                            {tab === "verify"
                                ? "Verify to mark attendance"
                                : "Register to mark attendance"}
                        </h2>
                        <p className="text-xs text-[#4B5563] mt-2">
                            {tab === "verify"
                                ? "Enter the OTP sent to your registered WhatsApp number."
                                : "Walk-in candidate? Register your details in one step."}
                        </p>
                    </div>

                    <div className="grid grid-cols-2 gap-0 mb-6 border-b border-[#0F1F38]/10">
                        <button
                            data-testid="tab-verify"
                            onClick={() => setTab("verify")}
                            disabled={busy}
                            className={`py-3 text-sm font-medium flex items-center justify-center gap-2 border-b-2 -mb-px transition-colors ${
                                tab === "verify"
                                    ? "border-[#0F1F38] text-[#0F1F38]"
                                    : "border-transparent text-[#0F1F38]/50 hover:text-[#0F1F38]"
                            }`}
                        >
                            <ShieldCheck size={14} strokeWidth={1.5} />
                            Verify OTP
                        </button>
                        <button
                            data-testid="tab-register"
                            onClick={() => setTab("register")}
                            disabled={busy}
                            className={`py-3 text-sm font-medium flex items-center justify-center gap-2 border-b-2 -mb-px transition-colors ${
                                tab === "register"
                                    ? "border-[#0F1F38] text-[#0F1F38]"
                                    : "border-transparent text-[#0F1F38]/50 hover:text-[#0F1F38]"
                            }`}
                        >
                            <UserPlus size={14} strokeWidth={1.5} />
                            Register Now
                        </button>
                    </div>

                    {tab === "verify" && (
                        <fieldset disabled={busy} className="disabled:opacity-70">
                            <form
                                data-testid="verify-form"
                                onSubmit={submitVerify}
                                className="space-y-4"
                            >
                                <div>
                                    <Label className="text-[10px] uppercase tracking-label text-[#0F1F38]/60">
                                        Phone Number
                                    </Label>
                                    <PhoneInput
                                        value={vPhone}
                                        onChange={setVPhone}
                                        onValidChange={setVPhoneValid}
                                        testid="verify-phone"
                                        disabled={busy}
                                    />
                                </div>
                                <div>
                                    <Label className="text-[10px] uppercase tracking-label text-[#0F1F38]/60">
                                        OTP
                                    </Label>
                                    <OtpInput
                                        value={vOtp}
                                        onChange={setVOtp}
                                        onValidChange={setVOtpValid}
                                        testid="verify-otp"
                                        disabled={busy}
                                    />
                                </div>
                                <button
                                    data-testid="btn-verify"
                                    type="submit"
                                    disabled={busy || !vPhoneValid || !vOtpValid}
                                    className="w-full bg-[#0F1F38] text-[#F7F5F0] py-3 text-sm font-medium inline-flex items-center justify-center gap-2 hover:bg-[#1A3254] disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {busy ? (
                                        <Loader2 size={14} className="animate-spin" />
                                    ) : (
                                        <ShieldCheck size={14} />
                                    )}
                                    Verify Attendance
                                </button>
                                <p className="text-xs text-center text-[#4B5563]">
                                    Didn&apos;t receive an OTP?{" "}
                                    <button
                                        data-testid="link-to-register"
                                        type="button"
                                        onClick={() => setTab("register")}
                                        className="underline hover:text-[#0F1F38]"
                                        disabled={busy}
                                    >
                                        Register now
                                    </button>
                                </p>
                            </form>
                        </fieldset>
                    )}

                    {tab === "register" && (
                        <fieldset disabled={busy} className="disabled:opacity-70">
                            <form
                                data-testid="register-form"
                                onSubmit={submitRegister}
                                className="space-y-4"
                            >
                                <TextField
                                    label="Name *"
                                    testid="reg-name"
                                    value={reg.name}
                                    onChange={(v) => setReg({ ...reg, name: v })}
                                />
                                <div>
                                    <Label className="text-[10px] uppercase tracking-label text-[#0F1F38]/60">
                                        Phone Number *
                                    </Label>
                                    <PhoneInput
                                        value={reg.phone}
                                        onChange={(v) => setReg({ ...reg, phone: v })}
                                        onValidChange={setRegPhoneValid}
                                        testid="reg-phone"
                                        disabled={busy}
                                    />
                                </div>
                                <TextField
                                    label="Email *"
                                    testid="reg-email"
                                    value={reg.email}
                                    onChange={(v) => setReg({ ...reg, email: v })}
                                    type="email"
                                />
                                <div>
                                    <Label className="text-[10px] uppercase tracking-label text-[#0F1F38]/60">
                                        Job Role
                                    </Label>
                                    <Select
                                        value={reg.role || ""}
                                        onValueChange={(v) =>
                                            setReg({ ...reg, role: v })
                                        }
                                    >
                                        <SelectTrigger data-testid="reg-role">
                                            <SelectValue placeholder="Select job role" />
                                        </SelectTrigger>
                                        <SelectContent className="max-h-72">
                                            {jobRoles.map((r) => (
                                                <SelectItem key={r.name} value={r.name}>
                                                    {r.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-[10px] uppercase tracking-label text-[#0F1F38]/60">
                                        College Name
                                    </Label>
                                    <Select
                                        value={reg.college_name || ""}
                                        onValueChange={(v) =>
                                            setReg({ ...reg, college_name: v })
                                        }
                                    >
                                        <SelectTrigger data-testid="reg-college">
                                            <SelectValue placeholder="Select your college" />
                                        </SelectTrigger>
                                        <SelectContent className="max-h-72">
                                            {colleges.map((c) => (
                                                <SelectItem key={c.name} value={c.name}>
                                                    {c.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <TextField
                                    label="Degree"
                                    testid="reg-course"
                                    value={reg.course}
                                    onChange={(v) => setReg({ ...reg, course: v })}
                                />
                                <TextField
                                    label="Department"
                                    testid="reg-branch"
                                    value={reg.branch}
                                    onChange={(v) => setReg({ ...reg, branch: v })}
                                />
                                <button
                                    data-testid="btn-register"
                                    type="submit"
                                    disabled={busy || !regPhoneValid}
                                    className="w-full bg-[#0F1F38] text-[#F7F5F0] py-3 text-sm font-medium inline-flex items-center justify-center gap-2 hover:bg-[#1A3254] disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {busy ? (
                                        <Loader2 size={14} className="animate-spin" />
                                    ) : (
                                        <UserPlus size={14} />
                                    )}
                                    Register
                                </button>
                            </form>
                        </fieldset>
                    )}
                </div>
            </div>
        </SiteChrome>
    );
}

function TextField({ label, testid, value, onChange, type = "text" }) {
    return (
        <div>
            <Label className="text-[10px] uppercase tracking-label text-[#0F1F38]/60">
                {label}
            </Label>
            <Input
                data-testid={testid}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                required={label.includes("*")}
            />
        </div>
    );
}
